import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('interview_login_token')}`,
  },
});

// Request interceptor - Add token to requests (use both keys so login works without reload)
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token') || localStorage.getItem('interview_login_token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
// Note: This interceptor does NOT modify the response structure
// response.data will contain exactly what the server sends
axiosInstance.interceptors.response.use(
  (response) => {
    // Return response as-is, ensuring response.data contains server's JSON response
    // Server response structure: { success: boolean, message: string, data: {...} }
    // This will be available as response.data in components
    console.log('Response from axiosInterceptor', response.data,"<");
    return response;
  },
  (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Clear all auth tokens so next request doesn't reuse invalid token
      localStorage.removeItem('token');
      localStorage.removeItem('interview_login_token');
      localStorage.removeItem('interview_login_user');

      // Redirect to login if not already there
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        toast.error('Session expired. Please login again.');
        window.location.href = '/login';
      }

      return Promise.reject(error);
    }

    // Handle other errors
    if (error.response) {
      const status = error.response.status;
      
      // Only show toast for server errors (500+) and network errors
      // Let individual components handle 4xx errors (validation, not found, etc.)
      if (status >= 500) {
        const message = (error.response.data as any)?.error || 'Server error. Please try again later.';
        toast.error(message);
      }
      // 401 is already handled above, so we skip it here
    } else if (error.request) {
      // Network error - no response received
      toast.error('Network error. Please check your connection.');
    } else {
      // Request setup error
      toast.error('An unexpected error occurred.');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

